'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardContent } from '../components/ui/Card';
import { FileXls, Lock, User, Envelope, ArrowRight, CheckCircle, WarningCircle, Ticket } from '@phosphor-icons/react';
import { authApi } from '../services/api';
import { Footer } from './components/Footer';
import { getRegionProfile } from '../config/region';
import { getPurchaseUrl } from '../config/purchase';
import { useToastActions } from '../components/ui/Toast';

// Magnetic Button Component
function MagneticButton({ children, ...props }: React.ComponentProps<typeof Button>) {
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <Button {...props}>{children}</Button>
    </motion.div>
  );
}

export function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { success: toastSuccess } = useToastActions();
  const profile = getRegionProfile();
  const isCN = profile.region === 'cn';

  // Form data
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    nickname: '',
    password: '',
    confirmPassword: '',
    // CN Region: special code for plan activation
    specialCode: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  // CN Region: code validation state
  const [isValidatingCode, setIsValidatingCode] = useState(false);
  const [codeValidationResult, setCodeValidationResult] = useState<{
    valid: boolean;
    plan?: string;
    duration?: number;
    message?: string;
  } | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
    
    // Clear validation result when code changes
    if (name === 'specialCode') {
      setCodeValidationResult(null);
    }
  };

  // CN Region: Validate special code
  const validateSpecialCode = async () => {
    if (!formData.specialCode.trim()) {
      return;
    }

    setIsValidatingCode(true);
    setCodeValidationResult(null);

    try {
      const response = await authApi.validateSpecialCode(formData.specialCode.trim());
      if (response.code === 0 && response.data) {
        setCodeValidationResult({
          valid: response.data.valid,
          plan: response.data.plan,
          duration: response.data.durationMonths,
          message: response.data.message,
        });
        if (response.data.valid) {
          toastSuccess(t('auth.codeValid', { 
            plan: response.data.plan, 
            duration: response.data.durationMonths 
          }));
        }
      } else {
        setCodeValidationResult({
          valid: false,
          message: response.message || t('auth.invalidCode'),
        });
      }
    } catch (err: unknown) {
      setCodeValidationResult({
        valid: false,
        message: err instanceof Error ? err.message : t('auth.codeValidationFailed'),
      });
    } finally {
      setIsValidatingCode(false);
    }
  };

  const validateForm = () => {
    if (formData.username.length < 3) {
      setError(t('auth.usernameTooShort'));
      return false;
    }
    // CN Region: email is optional, but if provided must be valid
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError(t('auth.invalidEmail'));
      return false;
    }
    if (formData.password.length < 6) {
      setError(t('auth.passwordTooShort'));
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.passwordMismatch'));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const registerData: {
        username: string;
        email: string;
        password: string;
        nickname?: string;
        specialCode?: string;
      } = {
        username: formData.username,
        email: formData.email || `${formData.username}@placeholder.com`, // CN region: generate placeholder email if not provided
        password: formData.password,
        nickname: formData.nickname || formData.username,
      };

      // CN Region: include special code if provided and validated
      if (isCN && formData.specialCode.trim() && codeValidationResult?.valid) {
        registerData.specialCode = formData.specialCode.trim();
      }

      const response = await authApi.register(registerData);

      if (response.code !== 0) {
        throw new Error(response.message || t('auth.registerFailed'));
      }

      setRegisteredEmail(formData.email || formData.username);
      setIsSuccess(true);
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : t('auth.registerFailedRetry');
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  // Stagger animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring' as const,
        stiffness: 100,
        damping: 20,
      },
    },
  };

  return (
    <div className="min-h-[100dvh] bg-zinc-50 flex">
      {/* Left Side - Branding (Asymmetric Layout) */}
      <motion.div 
        className="hidden lg:flex lg:w-1/2 bg-zinc-900 relative overflow-hidden"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)`,
            backgroundSize: '32px 32px',
          }} />
        </div>
        
        {/* Floating Elements */}
        <motion.div
          className="absolute top-1/4 left-1/4 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-zinc-700/30 rounded-full blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-16 xl:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/10 mb-8">
              <FileXls className="w-5 h-5 text-emerald-400" weight="fill" />
              <span className="text-sm font-medium text-white/90">{t('app.name')}</span>
            </div>
            
            <h1 className="text-5xl xl:text-6xl font-bold text-white tracking-tight leading-[1.1] mb-6">
              {t('auth.registerTitle')}
              <br />
              <span className="text-emerald-400">{t('auth.registerSubtitle')}</span>
            </h1>
            
            <p className="text-lg text-zinc-400 max-w-md leading-relaxed">
              {t('auth.registerDescription')}
            </p>
          </motion.div>

          {/* Features */}
          <motion.div 
            className="flex flex-col gap-4 mt-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-400" weight="fill" />
              </div>
              <span className="text-zinc-300">{t('auth.freeRegister')}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-400" weight="fill" />
              </div>
              <span className="text-zinc-300">{t('auth.aiPowered')}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-400" weight="fill" />
              </div>
              <span className="text-zinc-300">{t('auth.secure')}</span>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Right Side - Register Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <motion.div
          className="w-full max-w-md"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Mobile Logo */}
          <motion.div variants={itemVariants} className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-zinc-900 mb-4 overflow-hidden">
              <img src="/logo.svg" alt="Logo" className="w-8 h-8" />
            </div>
            <h1 className="text-2xl font-bold text-zinc-900">{t('app.name')}</h1>
          </motion.div>

          <AnimatePresence mode="wait">
            {!isSuccess ? (
              <motion.div variants={itemVariants} key="form">
                <Card variant="elevated" className="overflow-hidden">
                  <CardContent className="p-8">
                    <div className="mb-8">
                      <h2 className="text-2xl font-semibold text-zinc-900 tracking-tight">
                        {t('auth.createAccount')}
                      </h2>
                      <p className="text-zinc-500 mt-2 text-sm">
                        {t('auth.fillInfo')}
                      </p>
                    </div>

                    {error && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600 flex items-start gap-2"
                      >
                        <WarningCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <span>{error}</span>
                      </motion.div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5">
                      <Input
                        label={t('auth.username')}
                        name="username"
                        type="text"
                        value={formData.username}
                        onChange={handleChange}
                        leftIcon={<User className="w-5 h-5" />}
                        placeholder={t('auth.placeholderUsername')}
                        required
                      />

                      {/* CN Region: Email is optional */}
                      <Input
                        label={t('auth.email') + (isCN ? ` (${t('auth.optional')})` : '')}
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        leftIcon={<Envelope className="w-5 h-5" />}
                        placeholder={t('auth.placeholderEmail')}
                        required={!isCN}
                      />

                      <Input
                        label={t('auth.password')}
                        name="password"
                        type="password"
                        value={formData.password}
                        onChange={handleChange}
                        leftIcon={<Lock className="w-5 h-5" />}
                        placeholder={t('auth.placeholderPassword')}
                        required
                      />

                      <Input
                        label={t('auth.confirmPassword')}
                        name="confirmPassword"
                        type="password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        leftIcon={<Lock className="w-5 h-5" />}
                        placeholder={t('auth.placeholderConfirmPassword')}
                        required
                      />

                      {/* CN Region: Special Code Input */}
                      {isCN && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <label className="text-sm font-medium text-zinc-700">
                              {t('auth.specialCode')} ({t('auth.optional')})
                            </label>
                            <a 
                              href={getPurchaseUrl()}
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-emerald-600 hover:text-emerald-700"
                            >
                              {t('auth.buyCode')}
                            </a>
                          </div>
                          <div className="flex items-stretch gap-2">
                            <Input
                              name="specialCode"
                              type="text"
                              value={formData.specialCode}
                              onChange={handleChange}
                              leftIcon={<Ticket className="w-5 h-5" />}
                              placeholder={t('auth.placeholderSpecialCode')}
                              className="flex-1"
                            />
                            <Button
                              type="button"
                              variant="secondary"
                              onClick={validateSpecialCode}
                              isLoading={isValidatingCode}
                              disabled={!formData.specialCode.trim()}
                              className="whitespace-nowrap px-4 h-auto"
                            >
                              {t('auth.validate')}
                            </Button>
                          </div>
                          
                          {/* Code validation result */}
                          {codeValidationResult && (
                            <motion.div
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              className={`p-3 rounded-lg text-sm ${
                                codeValidationResult.valid 
                                  ? 'bg-emerald-50 border border-emerald-100 text-emerald-700' 
                                  : 'bg-red-50 border border-red-100 text-red-600'
                              }`}
                            >
                              {codeValidationResult.valid ? (
                                <div className="flex items-center gap-2">
                                  <CheckCircle className="w-4 h-4" weight="fill" />
                                  <span>
                                    {t('auth.codeValidDescription', { 
                                      plan: codeValidationResult.plan,
                                      duration: codeValidationResult.duration 
                                    })}
                                  </span>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <WarningCircle className="w-4 h-4" />
                                  <span>{codeValidationResult.message}</span>
                                </div>
                              )}
                            </motion.div>
                          )}

                          {/* Purchase hint */}
                          <p className="text-xs text-zinc-500">
                            {t('auth.specialCodeHint')}
                          </p>
                        </div>
                      )}

                      <div className="pt-2">
                        <MagneticButton
                          type="submit"
                          variant="primary"
                          size="lg"
                          isLoading={isLoading}
                          className="w-full"
                          rightIcon={<ArrowRight className="w-5 h-5" />}
                        >
                          {t('auth.register')}
                        </MagneticButton>
                      </div>
                    </form>

                    <div className="mt-6 pt-6 border-t border-zinc-100 text-center">
                      <p className="text-sm text-zinc-500">
                        {t('auth.haveAccount')}{' '}
                        <Link to="/login" className="text-emerald-600 hover:text-emerald-700 font-medium">
                          {t('auth.loginNow')}
                        </Link>
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ) : (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3 }}
              >
                <Card variant="elevated" className="overflow-hidden">
                  <CardContent className="p-8 text-center">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                      className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-100 flex items-center justify-center"
                    >
                      <CheckCircle className="w-10 h-10 text-emerald-600" weight="fill" />
                    </motion.div>

                    <h2 className="text-2xl font-semibold text-zinc-900 tracking-tight mb-2">
                      {t('auth.registerSuccess')}
                    </h2>
                    <p className="text-zinc-500 mb-6">
                      {isCN 
                        ? t('auth.registerSuccessCN', { username: registeredEmail })
                        : t('auth.verifyEmail', { email: registeredEmail })
                      }
                    </p>

                    {!isCN && (
                      <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 mb-6 text-left">
                        <div className="flex items-start gap-3">
                          <WarningCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                          <div className="text-sm text-amber-800">
                            <p className="font-medium mb-1">{t('auth.verifyPrompt')}</p>
                            <p>{t('auth.verifyDescription')}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="space-y-3">
                      <MagneticButton
                        variant="primary"
                        size="lg"
                        className="w-full"
                        onClick={() => navigate('/login')}
                      >
                        {t('auth.goToLogin')}
                      </MagneticButton>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Footer */}
          <Footer />
        </motion.div>
      </div>
    </div>
  );
}
